import { useState, useRef, useEffect } from "react";
import { Camera, X, Check, Loader2, RefreshCw, Sparkles, ImagePlus, Zap, ZapOff } from "lucide-react";
import { toast } from "sonner";
import { uploadDocument } from "../lib/api";
import { useCamera } from "../lib/useCamera";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const Filing = ({ progress, total }) => (
  <div className="py-10 flex flex-col items-center text-center">
    <div className="relative h-16 w-16 grid place-items-center">
      <div className="absolute inset-0 rounded-2xl ai-shimmer" />
      <Sparkles className="h-7 w-7 text-primary relative" />
    </div>
    <p className="mt-5 font-medium flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Reading & filing photo {Math.min(progress + 1, total)} of {total}</p>
    <div className="mt-3 h-1.5 w-48 rounded-full bg-border overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${(progress / total) * 100}%` }} /></div>
  </div>
);

export const CameraCaptureModal = ({ open, onClose, onUploaded }) => {
  const videoRef = useRef(null);
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);
  const cam = useCamera(videoRef);
  const [shots, setShots] = useState([]); // {blob, url}
  const [flash, setFlash] = useState(false);
  const [filing, setFiling] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    cam.start();
    return () => {
      cam.stop();
      setShots((prev) => { prev.forEach((s) => URL.revokeObjectURL(s.url)); return []; });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const snap = async () => {
    const blob = await cam.snap();
    if (!blob) return toast.error("Camera is still warming up — try again in a second");
    setFlash(true);
    setTimeout(() => setFlash(false), 140);
    setShots((s) => [...s, { blob, url: URL.createObjectURL(blob) }]);
  };
  const addFiles = (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setShots((s) => [...s, ...list.map((f) => ({ blob: f, url: URL.createObjectURL(f) }))]);
  };
  const removeShot = (i) => setShots((s) => { URL.revokeObjectURL(s[i].url); return s.filter((_, idx) => idx !== i); });

  const fileAll = async () => {
    if (!shots.length) return;
    setFiling(true);
    setProgress(0);
    let ok = 0;
    for (let i = 0; i < shots.length; i++) {
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const file = new File([shots[i].blob], `snap-${stamp}-${i + 1}.jpg`, { type: "image/jpeg" });
      try {
        const doc = await uploadDocument(file, "camera");
        ok += 1;
        toast.success(doc.auto_cropped ? `Straightened & filed under ${doc.category}` : `Filed under ${doc.category}`, { description: doc.title });
        onUploaded?.(doc);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Couldn't file photo ${i + 1}`);
      }
      setProgress(i + 1);
    }
    setFiling(false);
    if (ok > 0) onClose();
  };

  const portrait = cam.size.h > cam.size.w;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !filing && onClose()}>
      <DialogContent data-testid="camera-capture-modal" className="p-0 overflow-hidden flex flex-col gap-0 max-sm:inset-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:left-0 max-sm:top-0 max-sm:h-[100dvh] max-sm:max-w-none max-sm:w-screen max-sm:rounded-none sm:max-w-lg sm:max-h-[92vh]">
        <DialogHeader className="px-5 pt-5 pb-2 shrink-0">
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><Camera className="h-5 w-5 text-primary" /> Snap a document</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">Fill the frame with the page, hold still, tap the big button. Snap several pages — we straighten, crop and file them all at once.</DialogDescription>
        </DialogHeader>

        {filing ? <Filing progress={progress} total={shots.length} /> : (
          <>
            <div className="relative bg-black flex-1 min-h-0 sm:flex-none sm:mx-5 sm:rounded-2xl overflow-hidden" style={{ aspectRatio: window.innerWidth >= 640 ? "4 / 3" : undefined }} data-testid="camera-viewport">
              <video ref={videoRef} playsInline muted autoPlay className={`absolute inset-0 w-full h-full ${portrait ? "object-cover" : "object-contain"} ${cam.ready ? "" : "opacity-0"}`} />
              {flash && <div className="absolute inset-0 bg-white/80 pointer-events-none" />}
              {!cam.ready && (
                <div className="absolute inset-0 grid place-items-center text-center px-6 text-white/85">
                  {cam.error ? (
                    <div>
                      <Camera className="h-8 w-8 mx-auto opacity-60" />
                      <p className="mt-3 text-sm" data-testid="camera-error">{cam.error}</p>
                      <div className="mt-4 flex flex-wrap justify-center gap-2">
                        <Button size="sm" onClick={() => cameraInputRef.current?.click()} className="gap-1.5 rounded-full" data-testid="camera-native-btn"><Camera className="h-3.5 w-3.5" /> Take photo</Button>
                        <Button size="sm" variant="secondary" onClick={cam.start} className="gap-1.5 rounded-full"><RefreshCw className="h-3.5 w-3.5" /> Try again</Button>
                      </div>
                    </div>
                  ) : <Loader2 className="h-6 w-6 animate-spin" />}
                </div>
              )}
              {cam.ready && (
                <>
                  <div className="absolute inset-x-5 top-5 bottom-28 pointer-events-none rounded-xl border-2 border-dashed border-white/40" />
                  {cam.torch.supported && (
                    <button type="button" onClick={cam.toggleTorch} data-testid="camera-torch-btn" className="absolute top-3 right-3 h-10 w-10 rounded-full bg-black/50 text-white grid place-items-center backdrop-blur">
                      {cam.torch.on ? <Zap className="h-4 w-4" /> : <ZapOff className="h-4 w-4" />}
                    </button>
                  )}
                  {shots.length > 0 && <span className="absolute top-3 left-3 rounded-full bg-black/50 text-white text-xs px-2.5 py-1 backdrop-blur" data-testid="camera-shot-count">{shots.length} page{shots.length === 1 ? "" : "s"}</span>}
                  <div className="absolute inset-x-0 bottom-0 pb-5 pt-10 bg-gradient-to-t from-black/70 to-transparent flex items-center justify-center gap-8">
                    <button type="button" data-testid="camera-library-btn" onClick={() => libraryInputRef.current?.click()} className="h-12 w-12 rounded-full bg-white/15 text-white grid place-items-center backdrop-blur" title="Choose from library"><ImagePlus className="h-5 w-5" /></button>
                    <button type="button" data-testid="camera-shutter-btn" onClick={snap} aria-label="Take photo" className="h-20 w-20 rounded-full bg-white border-[5px] border-white/40 shadow-xl active:scale-90 transition-transform grid place-items-center">
                      <span className="h-14 w-14 rounded-full bg-primary" />
                    </button>
                    <button type="button" data-testid="camera-native-btn" onClick={() => cameraInputRef.current?.click()} className="h-12 w-12 rounded-full bg-white/15 text-white grid place-items-center backdrop-blur" title="Use your phone's camera app"><Camera className="h-5 w-5" /></button>
                  </div>
                </>
              )}
            </div>
            <input ref={cameraInputRef} data-testid="camera-file-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            <input ref={libraryInputRef} data-testid="camera-library-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />

            <div className="px-5 pb-5 pt-3 shrink-0 bg-background">
              <div className="flex items-center gap-2 overflow-x-auto pb-1 min-h-[4.5rem]">
                {shots.map((s, i) => (
                  <div key={s.url} data-testid="camera-shot-thumb" className="relative h-16 w-16 shrink-0">
                    <img src={s.url} alt="" className="h-16 w-16 rounded-xl object-cover border border-border" />
                    <button type="button" onClick={() => removeShot(i)} aria-label="Remove photo" className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-foreground text-background grid place-items-center"><X className="h-3 w-3" /></button>
                  </div>
                ))}
                {shots.length === 0 && <span className="text-xs text-muted-foreground pl-1">No pages yet — tap the round button to snap, or the small camera to use your phone's camera app.</span>}
              </div>
              <Button data-testid="camera-file-all-btn" onClick={fileAll} disabled={!shots.length} className="mt-2 w-full h-12 gap-2 bg-primary hover:bg-primary/90 rounded-full text-base">
                <Check className="h-4 w-4" /> {shots.length ? `File ${shots.length} page${shots.length > 1 ? "s" : ""} with AI` : "Snap or choose a photo"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
